import { createContext, useContext, useState } from "react";

export const translations = {
  en: {
    preloader: { igniting: "Igniting" },
    nav: {
      services: "Services",
      whyus: "Why Us",
      environment: "Environment",
      contact: "Contact",
      cta: "Start a project",
    },
    hero: {
      badge: "AI Studio — Websites, Automations, Social",
      line1: "WE DON'T JUST",
      line2: "BUILD AI.",
      line3: "WE BUILD FUTURES.",
      subtext:
        "Fenrion AI designs conversion-obsessed websites, deploys automations that work while you sleep, and runs social media that actually grows your brand.",
      ctaStart: "Start a project",
      ctaExplore: "Explore services",
      stat1: "Services, one engine",
      stat2: "Dedicated team",
      stat3: "AI operations",
      scroll: "Scroll",
    },
    marquee: {
      items: ["FENRION AI", "WEBSITE CREATION", "AUTOMATIONS", "SOCIAL MEDIA MANAGEMENT"],
    },
    services: {
      eyebrow: "What we do",
      heading: "Three services. One growth engine.",
      items: [
        {
          title: "Website Creation",
          description:
            "Conversion-obsessed websites designed, built, and shipped end to end. Every pixel earns its place — from the first wireframe to the final deploy.",
          deliverables: ["Conversion-first design", "Blazing performance", "SEO foundations", "CMS & analytics"],
        },
        {
          title: "Automations",
          description:
            "AI agents and workflows that remove repetitive work, connect your tools, and keep operations running around the clock.",
          deliverables: ["AI agents", "Workflow orchestration", "Tool integrations"],
        },
        {
          title: "Social Media Management",
          description:
            "AI-assisted content calendars, consistent posting, and analytics that turn followers into customers.",
          deliverables: ["Content calendars", "Scheduled posting", "Growth analytics"],
        },
      ],
    },
    playground: {
      eyebrow: "The playground",
      heading: "Don't take our word for it. Play with it.",
      terminalTitle: "fenrion.agent — live",
      terminal: [
        { t: "$ fenrion agent deploy --workflow lead-followup", c: "cmd" },
        { t: "> scanning inbox... 142 new leads found", c: "info" },
        { t: "> drafting personalized follow-ups...", c: "info" },
        { t: "> 142 emails queued. 0 human hours spent.", c: "ok" },
        { t: "$ fenrion agent deploy --workflow social-engine", c: "cmd" },
        { t: "> generating 30-day content calendar...", c: "info" },
        { t: "> scheduling posts across 4 channels...", c: "info" },
        { t: "> done. your brand just got louder.", c: "ok" },
      ],
      roiTitle: "Automation return model (ARM)",
      roiHours: "Repetitive hours / person / week",
      roiTeam: "Team members affected",
      roiOut1: "returned per year",
      roiOut2: "estimated value unlocked",
      roiNote: "A visualization, not a warranty. €45/h blended rate.",
    },
    whyus: {
      eyebrow: "The Fenrion manifesto",
      heading: "Why ambitious brands choose us.",
      chapters: [
        {
          title: "Strategy before syntax",
          text: "Every engagement starts with your business model, not a tech stack. We map where AI creates leverage, then build only what moves the needle.",
        },
        {
          title: "AI-native, not AI-bolted",
          text: "We don't sprinkle chatbots on legacy thinking. Intelligence is designed into the core of every website, workflow, and campaign we ship.",
        },
        {
          title: "Speed is a feature",
          text: "Weeks, not quarters. Our AI-accelerated process compresses timelines without compressing craft — you launch while competitors are still in meetings.",
        },
        {
          title: "Measured, not guessed",
          text: "Every build ships with analytics wired in. Conversions, hours saved, followers gained — you see the return in numbers, not adjectives.",
        },
      ],
    },
    environment: {
      eyebrow: "The Fenrion environment",
      heading: "One hub. Every deliverable, every number.",
      copy: "From day one, your project runs inside a single centralized environment — built around your business. This is where we deliver, measure, and refine everything we create together.",
      pillars: [
        {
          title: "Everything in one place",
          text: "Deliverables, files, timelines, and analytics live in a single environment — no lost emails, no scattered links.",
        },
        {
          title: "Progress you can see",
          text: "Follow every build in real time. You always know what's done, what's next, and what's blocking — before you ask.",
        },
        {
          title: "Direct line to the team",
          text: "Feedback and decisions happen inside the same hub, so context never gets lost between tools.",
        },
      ],
      dashboard: "hub.fenrion.ai — your project",
      menu: ["Overview", "Deliverables", "Automations", "Social", "Reports"],
      project: "Website Creation",
      tile1: "automations live",
      tile2: "content scheduled",
      recentActivity: "Recent activity",
      activity: [
        { text: "Homepage design approved", time: "09:41" },
        { text: "Lead-followup agent deployed", time: "Yesterday" },
        { text: "October content calendar ready", time: "Yesterday" },
        { text: "New analytics report available", time: "Mon" },
      ],
      caption: "Illustrative preview of the environment every Fenrion project runs in.",
    },
    contact: {
      eyebrow: "Contact",
      heading: "Let's build your unfair advantage.",
      copy: "Tell us where you want to grow. We'll come back within 24 hours with a plan — and a timeline that will surprise you.",
      name: "Name",
      email: "Email",
      service: "Service",
      details: "Project details",
      namePlaceholder: "Jane Cooper",
      emailPlaceholder: "jane@company.com",
      detailsPlaceholder: "Tell us about your goals...",
      services: ["Website Creation", "Automations", "Social Media Management", "Not sure yet"],
      submit: "Send enquiry",
      sending: "Sending",
      success: "Enquiry sent — we'll reply within 24 hours.",
      error: "Something went wrong. Please try again.",
    },
    footer: {
      tagline:
        "An AI studio building websites, automations, and social engines for brands that refuse to be ordinary.",
      services: "Services",
      company: "Company",
      websites: "Website Creation",
      automations: "Automations",
      social: "Social Media",
      whyus: "Why Us",
      environment: "Environment",
      contact: "Contact",
      backToTop: "Back to top",
      rights: "All rights reserved.",
      built: "Built with intelligence, shipped with obsession.",
    },
    chat: {
      title: "Fen — Fenrion AI",
      online: "Online",
      greeting:
        "Hey, I'm Fen — Fenrion AI's concierge. Ask me anything about our websites, automations, or social media services.",
      suggestions: [
        "What services do you offer?",
        "How much does a website cost?",
        "How do automations work?",
      ],
      placeholder: "Ask about our services...",
      error: "Connection issue — please try again.",
    },
  },
  pt: {
    preloader: { igniting: "A ignitar" },
    nav: {
      services: "Serviços",
      whyus: "Porquê Nós",
      environment: "Ambiente",
      contact: "Contacto",
      cta: "Começar um projeto",
    },
    hero: {
      badge: "Estúdio de IA — Websites, Automações, Social",
      line1: "NÃO CRIAMOS",
      line2: "APENAS IA.",
      line3: "CRIAMOS FUTUROS.",
      subtext:
        "A Fenrion AI cria websites obcecados por conversão, implementa automações que trabalham enquanto dormes e gere redes sociais que realmente fazem crescer a tua marca.",
      ctaStart: "Começar um projeto",
      ctaExplore: "Explorar serviços",
      stat1: "Serviços, um motor",
      stat2: "Equipa dedicada",
      stat3: "Operações de IA",
      scroll: "Explora",
    },
    marquee: {
      items: ["FENRION AI", "CRIAÇÃO DE WEBSITES", "AUTOMAÇÕES", "GESTÃO DE REDES SOCIAIS"],
    },
    services: {
      eyebrow: "O que fazemos",
      heading: "Três serviços. Um motor de crescimento.",
      items: [
        {
          title: "Criação de Websites",
          description:
            "Websites obcecados por conversão, desenhados, construídos e lançados de ponta a ponta. Cada píxel merece o seu lugar — do primeiro wireframe ao deploy final.",
          deliverables: ["Design focado em conversão", "Performance relâmpago", "Fundações SEO", "CMS e analítica"],
        },
        {
          title: "Automações",
          description:
            "Agentes de IA e workflows que eliminam trabalho repetitivo, ligam as tuas ferramentas e mantêm as operações a correr sem parar.",
          deliverables: ["Agentes de IA", "Orquestração de workflows", "Integrações de ferramentas"],
        },
        {
          title: "Gestão de Redes Sociais",
          description:
            "Calendários de conteúdo assistidos por IA, publicação consistente e analítica que transforma seguidores em clientes.",
          deliverables: ["Calendários de conteúdo", "Publicação agendada", "Analítica de crescimento"],
        },
      ],
    },
    playground: {
      eyebrow: "O playground",
      heading: "Não acredites só na nossa palavra. Experimenta.",
      terminalTitle: "fenrion.agent — ao vivo",
      terminal: [
        { t: "$ fenrion agent deploy --workflow lead-followup", c: "cmd" },
        { t: "> a analisar a inbox... 142 novos leads encontrados", c: "info" },
        { t: "> a escrever follow-ups personalizados...", c: "info" },
        { t: "> 142 emails na fila. 0 horas humanas gastas.", c: "ok" },
        { t: "$ fenrion agent deploy --workflow social-engine", c: "cmd" },
        { t: "> a gerar calendário de conteúdo para 30 dias...", c: "info" },
        { t: "> a agendar publicações em 4 canais...", c: "info" },
        { t: "> feito. a tua marca acabou de ganhar volume.", c: "ok" },
      ],
      roiTitle: "Modelo de retorno de automação (ARM)",
      roiHours: "Horas repetitivas / pessoa / semana",
      roiTeam: "Membros da equipa afetados",
      roiOut1: "devolvidas por ano",
      roiOut2: "valor estimado desbloqueado",
      roiNote: "Uma visualização, não uma garantia. Taxa média de 45 €/h.",
    },
    whyus: {
      eyebrow: "O manifesto Fenrion",
      heading: "Porque as marcas ambiciosas nos escolhem.",
      chapters: [
        {
          title: "Estratégia antes de sintaxe",
          text: "Cada projeto começa com o teu modelo de negócio, não com uma tech stack. Mapeamos onde a IA cria alavancagem e construímos apenas o que move a agulha.",
        },
        {
          title: "IA nativa, não IA colada",
          text: "Não espalhamos chatbots sobre pensamento antigo. A inteligência é desenhada no núcleo de cada website, workflow e campanha que entregamos.",
        },
        {
          title: "A velocidade é uma funcionalidade",
          text: "Semanas, não trimestres. O nosso processo acelerado por IA comprime prazos sem comprimir qualidade — lanças enquanto os concorrentes ainda estão em reuniões.",
        },
        {
          title: "Medido, não adivinhado",
          text: "Cada entrega sai com analítica integrada. Conversões, horas poupadas, seguidores ganhos — vês o retorno em números, não em adjetivos.",
        },
      ],
    },
    environment: {
      eyebrow: "O ambiente Fenrion",
      heading: "Um só hub. Cada entrega, cada número.",
      copy: "Desde o primeiro dia, o teu projeto corre num único ambiente centralizado — construído à volta do teu negócio. É aqui que entregamos, medimos e refinamos tudo o que criamos juntos.",
      pillars: [
        {
          title: "Tudo num só lugar",
          text: "Entregas, ficheiros, prazos e analítica vivem num único ambiente — sem emails perdidos, sem links dispersos.",
        },
        {
          title: "Progresso que consegues ver",
          text: "Acompanha cada entrega em tempo real. Sabes sempre o que está feito, o que vem a seguir e o que está bloqueado — antes de perguntares.",
        },
        {
          title: "Linha direta com a equipa",
          text: "Feedback e decisões acontecem dentro do mesmo hub, para que o contexto nunca se perca entre ferramentas.",
        },
      ],
      dashboard: "hub.fenrion.ai — o teu projeto",
      menu: ["Visão geral", "Entregas", "Automações", "Social", "Relatórios"],
      project: "Criação de Website",
      tile1: "automações ativas",
      tile2: "conteúdo agendado",
      recentActivity: "Atividade recente",
      activity: [
        { text: "Design da homepage aprovado", time: "09:41" },
        { text: "Agente de follow-up implementado", time: "Ontem" },
        { text: "Calendário de conteúdo de outubro pronto", time: "Ontem" },
        { text: "Novo relatório de analítica disponível", time: "Seg" },
      ],
      caption: "Pré-visualização ilustrativa do ambiente onde corre cada projeto Fenrion.",
    },
    contact: {
      eyebrow: "Contacto",
      heading: "Vamos construir a tua vantagem injusta.",
      copy: "Diz-nos onde queres crescer. Respondemos em 24 horas com um plano — e um prazo que te vai surpreender.",
      name: "Nome",
      email: "Email",
      service: "Serviço",
      details: "Detalhes do projeto",
      namePlaceholder: "Joana Silva",
      emailPlaceholder: "joana@empresa.pt",
      detailsPlaceholder: "Fala-nos dos teus objetivos...",
      services: ["Criação de Website", "Automações", "Gestão de Redes Sociais", "Ainda não sei"],
      submit: "Enviar pedido",
      sending: "A enviar",
      success: "Pedido enviado — respondemos dentro de 24 horas.",
      error: "Algo correu mal. Tenta novamente.",
    },
    footer: {
      tagline:
        "Um estúdio de IA que constrói websites, automações e motores sociais para marcas que se recusam a ser comuns.",
      services: "Serviços",
      company: "Empresa",
      websites: "Criação de Websites",
      automations: "Automações",
      social: "Redes Sociais",
      whyus: "Porquê Nós",
      environment: "Ambiente",
      contact: "Contacto",
      backToTop: "Voltar ao topo",
      rights: "Todos os direitos reservados.",
      built: "Construído com inteligência, entregue com obsessão.",
    },
    chat: {
      title: "Fen — Fenrion AI",
      online: "Online",
      greeting:
        "Olá, sou o Fen — o concierge da Fenrion AI. Pergunta-me qualquer coisa sobre os nossos websites, automações ou gestão de redes sociais.",
      suggestions: [
        "Que serviços oferecem?",
        "Quanto custa um website?",
        "Como funcionam as automações?",
      ],
      placeholder: "Pergunta sobre os nossos serviços...",
      error: "Problema de ligação — tenta novamente.",
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(
    () => localStorage.getItem("fenrion-lang") || "en"
  );

  const setLang = (l) => {
    localStorage.setItem("fenrion-lang", l);
    setLangState(l);
  };

  const t = (path) => {
    const value = path.split(".").reduce((o, k) => (o ? o[k] : undefined), translations[lang]);
    return value !== undefined ? value : path;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
